import classnames from 'classnames';
import { get } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import modalActions from '@mm/state/modules/modalsState/actions';
import { Checkbox } from '@mm/components/src';
import { landmineNames } from '@mm/constants/landmine-names';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import {
  ChatbotChannel,
  DefaultResponseType,
} from '@mm/state/model/ChatbotModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { PageButtonConfig } from '@mm/state/model/ButtonModel';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { TYPES } from '../../../editor/features/build/components/widgets/constants/quickQuestion';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { useIsLeadGeneratorBuilderRoute } from '../../../modules/FreeLeadGenerator/use-is-lead-generator-builder-route';
import SingleAttributeInput from '../../components/SingleAttributeInput/SingleAttributeInput';
import {
  getQQUserResponseType,
  useQQWidgetHandlers,
} from '../../helpers/widget-helpers/use-qq-widget-handlers';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { useEditMode } from '../common/use-edit-mode';
import QuestionBotTextArea from './QuestionBotTextArea/QuestionBotTextArea';
import {
  getDropdownOption,
  QuestionReplyType,
} from './QuestionReplyType/QuestionReplyType';
import './QuestionWidget.pcss';
import { useCloseEditModeOnNonHotSpotClick } from './use-close-edit-mode-on-non-hotspot-click';
import { DropdownOption } from '../../components/PrimarySelect/PrimarySelect';
import { SmsWarningAlert } from '../common/SmsWarningAlert/SmsWarningAlert';
import { PlanBadge } from '../../components/ProBadge';
import { TooltipContainer } from '../../components/TooltipContainer/TooltipContainer';
import * as routes from '../../constants/routes';
import { ActivePathSelection } from '../common/ActivePathSelection/ActivePathSelection';
import { QuestionReminder } from './QuestionReminder/QuestionReminder';
import { showMMSMessageWarning } from '../../../editor/features/startBotPromoters/common/validateSmsRelatedWidgets';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import { QuestionOptIn } from './QuestionOptIn/QuestionOptIn';

type Props = {
  chatbotId: number;
  widget: WidgetModel;
  isExpanded?: boolean;
  draggableProvided?: DraggableProvided;
  disableDragging?: boolean;
  disableDeleting?: boolean;
  disableConditionalLogic?: boolean;
  forAds?: boolean;
  restrictToChannels?: ChatbotChannel[];
  platforms?: PlatformType[];
  isSmsRelated?: boolean;
  showAttributePicker?: boolean;
  showOnlyNameAttributes?: boolean;
  dialogueId?: number;
  forInstagram?: boolean;
  widgetsToExclude?: WidgetTypes[];
  showCountByChannel?: boolean;
  showAttributesStartWarning?: boolean;
  usedInSlideout?: boolean;
  pageWidgets: WidgetModel[];
} & WidgetContainerRefProp;

// tslint:disable-next-line:cyclomatic-complexity
export const QuestionWidget = (props: Props) => {
  const {
    widget,
    forAds,
    isExpanded,
    chatbotId,
    disableDragging,
    disableDeleting,
    disableConditionalLogic,
    restrictToChannels,
    draggableProvided,
    isSmsRelated,
    showAttributePicker = true,
    showAttributesStartWarning = false,
    showOnlyNameAttributes = false,
    dialogueId,
    forInstagram,
    widgetsToExclude,
    showCountByChannel,
    usedInSlideout,
    pageWidgets,
    platforms,
  } = props;
  const { history } = useRouter();

  const { editMode, setEditMode: setEditModeOriginal } = useEditMode(props);
  const qqWidgetHandlers = useQQWidgetHandlers();
  const qqResponseType = getQQUserResponseType(widget);
  const [isChangingResponseType, setIsChangingResponseType] = useState(false);
  const [selectedButton, setSelectedButton] = useState<number | undefined>();
  const [shouldSkip, setShouldSkip] = useState(false);
  const [shouldSaveToAttribute, setShouldSaveToAttribute] = useState(
    !!widget.config.variableId,
  );
  const menuItems = useSelector((state: StoreState) => state.mainMenu.entities);
  const canDisableComposerInput = menuItems.length > 0;
  const chatbot = useSelector(activeChatbotSelector);
  const notDeletedDialogues = useSelector(
    (state: StoreState) => state.page.entities,
  );

  const isPhoneResponseType = TYPES.PHONE === qqResponseType;

  const { getActiveButtonFromWidgetButtons, pathGraphLastUpdate } =
    useContext(PathGraphContext);
  const [isVisibleSmsWarningAlert, setIsVisibleSmsWarningAlert] =
    useState(false);
  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);
  const showMMSWarning =
    isSmsRelated && showMMSMessageWarning(pageWidgets, widget?.id);

  useEffect(() => {
    setIsVisibleSmsWarningAlert(false);

    if (widget.config.body) {
      if (widget.config.body.length > 160 && showMMSWarning) {
        setIsVisibleSmsWarningAlert(true);
      }
      setHasAttributesInBody(/{{.+}}/.test(widget.config.body));
    }
  }, [widget.config.body, showMMSWarning]);

  useEffect(() => {
    const newShouldSaveToAttribute = !!widget.config.variableId;
    const newShouldSkip = !!widget.config.skipSendWidget;
    if (shouldSkip !== newShouldSkip) {
      setShouldSkip(newShouldSkip);
    }
    if (newShouldSaveToAttribute !== shouldSaveToAttribute) {
      setShouldSaveToAttribute(newShouldSaveToAttribute);
    }
  }, [widget]);

  const [replyType, setReplyType] = useState<DropdownOption | undefined>(
    getDropdownOption(widget, forAds),
  );
  const onReplyTypeChange = (opt: DropdownOption) => setReplyType(opt);

  const setEditMode = (mode: boolean) => {
    setEditModeOriginal(mode);
  };

  const hotSpotRefs = useCloseEditModeOnNonHotSpotClick({
    editMode,
    setEditMode,
    selectedButton,
    setSelectedButton,
  });

  const onClickProcessedByTriggers = () => {
    qqWidgetHandlers.updateProcessedByTriggers(
      widget,
      !widget.config.processedByTriggers,
    );
  };

  const onClickSaveToAttribute = () => {
    setShouldSaveToAttribute(!shouldSaveToAttribute);

    if (shouldSaveToAttribute) {
      qqWidgetHandlers.clearAttribute(widget);
    }
  };

  const onClickSkip = () => {
    setShouldSkip(!shouldSkip);

    qqWidgetHandlers.updateShouldSkip(widget, !widget.config.skipSendWidget);
  };

  const classList = classnames('question-widget', {
    'question-widget--edit-mode': editMode,
  });

  const activeButton = useMemo(() => {
    return getActiveButtonFromWidgetButtons?.([...widget.buttons]);
  }, [getActiveButtonFromWidgetButtons, widget, pathGraphLastUpdate]);

  const nextLevelDialogueId = (activeButton?.config as PageButtonConfig)
    ?.pageId;

  const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();

  const isSmsOptIn = widget.type === WidgetTypes.SMS_OPT_IN_V2;
  const isSmsReply = widget.type === WidgetTypes.SMS_OPT_IN_REPLY;
  const deprecatedSmsOptIn = widget.type === WidgetTypes.SMS_OPT_IN;

  const editModeByWidgetKind = !deprecatedSmsOptIn && editMode;
  const attributeCheckboxLabel = deprecatedSmsOptIn
    ? 'Use value from attribute'
    : 'Save response to attribute';

  const processedByTriggersAtBotLevel = get(
    chatbot,
    'messagesSettings.processNotDefinedQqAnswerByTriggers',
  );

  const isDialogueDeleted = useMemo(
    () => !notDeletedDialogues.some(d => d.id === nextLevelDialogueId),
    [notDeletedDialogues, nextLevelDialogueId],
  );

  const freeOrSpecialPricePlan = useSelector(
    (state: StoreState) =>
      activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
  );

  const dispatch: Dispatch<StoreState> = useDispatch();

  const onToggleBlocksInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const blocksInput = e.target.checked;
    if (freeOrSpecialPricePlan) {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['COMPOSER_INPUT_DISABLE_CHECKBOX'],
          planFallback: PageBillingType.Pro,
        }),
      );
      e.preventDefault();
      return;
    }
    qqWidgetHandlers.setDisableComposerInput(widget, blocksInput);
  };

  const allowDisablingInput =
    !forAds &&
    replyType?.name === TYPES.MULTIPLE &&
    chatbot.noMatchResponse !== DefaultResponseType.NoTyping;

  return (
    <>
      <div className={classList}>
        <div className="question-widget__body">
          {isSmsRelated && (
            <SmsWarningAlert
              isVisible={isVisibleSmsWarningAlert}
              setIsVisibleSmsWarningAlert={setIsVisibleSmsWarningAlert}
            />
          )}
          <QuestionBotTextArea
            quickQuestion={widget}
            platforms={platforms}
            editMode={editModeByWidgetKind}
            setEditMode={setEditMode}
            questionInputHotSpotRef={hotSpotRefs.questionInputHotSpotRef}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            disableDragging={disableDragging}
            disableDeleting={disableDeleting || isSmsReply}
            disableConditionalLogic={disableConditionalLogic}
            isSmsOptIn={isSmsOptIn}
            canEditAnswers={!deprecatedSmsOptIn}
            canEditQuestion={!deprecatedSmsOptIn}
            draggableProvided={draggableProvided}
            showAttributePicker={showAttributePicker}
            showAttributesStartWarning={showAttributesStartWarning}
            showOnlyNameAttributes={showOnlyNameAttributes}
            dialogueId={dialogueId}
            showCountByChannel={showCountByChannel}
          />

          {(!nextLevelDialogueId || isDialogueDeleted) && (
            <>
              <div className="question-widget__response-container">
                <QuestionReplyType
                  forAds={forAds}
                  forInstagram={forInstagram}
                  bot={chatbot}
                  editMode={editModeByWidgetKind}
                  setEditMode={setEditMode}
                  quickQuestion={widget}
                  isSmsReply={isSmsReply}
                  isSmsOptIn={isSmsOptIn}
                  onChange={onReplyTypeChange}
                  qqResponseType={qqResponseType}
                  setIsChangingResponseType={setIsChangingResponseType}
                  questionReplyTypeHotSpotRef={
                    hotSpotRefs.questionReplyTypeHotSpotRef
                  }
                  usedInSlideout={usedInSlideout}
                />
                {allowDisablingInput && editMode && (
                  <div
                    className="question-reply-type__prevent-free-responses-checkbox-container"
                    ref={hotSpotRefs.freeFormResponsesHotSpotRef}
                  >
                    {!isLeadGeneratorRoute && (
                      <Checkbox
                        className="question-reply-type__prevent-free-responses-checkbox"
                        labelClassName="question-reply-type__prevent-free-responses-checkbox-label"
                        isChecked={widget.config.blocksInput || false}
                        disabled={!canDisableComposerInput}
                        onToggle={onToggleBlocksInput}
                        label={
                          <div className="question-reply-type__prevent-free-responses-label">
                            <span>Prevent free-form responses</span>
                          </div>
                        }
                      />
                    )}
                    <div className="question-reply-type__prevent-free-responses-warnings">
                      {freeOrSpecialPricePlan && (
                        <PlanBadge
                          className="question-reply-type__prevent-free-responses-badge"
                          billingType={PageBillingType.Pro}
                          size={10}
                        />
                      )}
                      {!canDisableComposerInput && (
                        <TooltipContainer
                          classNameContent="question-reply-type__prevent-free-responses-tooltip"
                          classNameAnchor="question-reply-type__prevent-free-responses-anchor"
                          classNameIconAnchor="fas fa-exclamation-triangle"
                          message={
                            <span
                              onClick={() =>
                                history.push(
                                  `${routes.REFRESH}${routes.CHATBOT_EDITOR}/${chatbot?.id}/build/main-menu`,
                                )
                              }
                              className="question-reply-type__prevent-free-responses-menu-redirect"
                            >
                              In order to disable free-form responses, you must
                              add a main menu.
                            </span>
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {qqResponseType === TYPES.TEXT &&
                !isChangingResponseType &&
                !processedByTriggersAtBotLevel && (
                  <div
                    className="question-bot-text-area__process-label"
                    ref={hotSpotRefs.processedByTriggersHotSpotRef}
                  >
                    <input
                      type="checkbox"
                      onChange={onClickProcessedByTriggers}
                      defaultChecked={!!widget.config.processedByTriggers}
                    />
                    Process input with Triggers
                  </div>
                )}
            </>
          )}
          {activeButton && (
            <ActivePathSelection
              chatbotId={chatbotId}
              widget={widget}
              activeButton={activeButton}
              isSmsRelated={isSmsRelated}
              className={selectedButton ? 'question-widget__button-open' : ''}
              widgetsToExclude={widgetsToExclude}
              platforms={platforms}
              usedInSlideout={usedInSlideout}
            />
          )}
        </div>

        {editMode && !isLeadGeneratorRoute && (
          <div
            className="question-widget__footer"
            ref={hotSpotRefs.questionAttributeSelectorHotSpotRef}
          >
            <div className="question-widget__footer-row">
              <div className="question-widget__attribute-wrapper">
                <div className="question-widget__attribute-heading">
                  <Checkbox
                    disabled={deprecatedSmsOptIn || isSmsReply}
                    label={attributeCheckboxLabel}
                    isChecked={shouldSaveToAttribute}
                    onClick={onClickSaveToAttribute}
                    className="question-widget__attribute-checkbox"
                  />
                </div>

                {shouldSaveToAttribute && (
                  <SingleAttributeInput
                    className="question-widget__select-attribute-container"
                    widget={widget}
                    qqResponseType={qqResponseType}
                    showErrorMessage
                    usedInSlideout={usedInSlideout}
                  />
                )}
                {isSmsReply && (
                  <div className="question-widget__attribute-heading question-widget__attribute-heading--skip">
                    <Checkbox
                      label="Skip if the attribute is already saved"
                      isChecked={shouldSkip}
                      onClick={onClickSkip}
                      className="question-widget__attribute-checkbox"
                    />
                  </div>
                )}
              </div>
            </div>
            <QuestionReminder
              chatbotId={chatbotId}
              widget={widget}
              className="question-widget__footer-row"
              platforms={platforms}
              usedInSlideout={usedInSlideout}
            />
            {isPhoneResponseType && (
              <QuestionOptIn
                chatbotId={chatbotId}
                widget={widget}
                className="question-widget__footer-row"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
